"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import Image from "next/image";
import { createPortal } from "react-dom";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import styles from "./productGallery.module.css";
import { ChevronUp, ChevronDown, X } from "lucide-react";

interface ProductGalleryProps {
  product: any;
}

const LENS_MIN = 40;
const LENS_DEFAULT = 150;

export default function ProductGallery({ product }: ProductGalleryProps) {
  const [activeImg, setActiveImg] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const wheelLock = useRef(false);
  const lensSizeRef = useRef(LENS_DEFAULT);
  const lensPosRef = useRef({ x: 0, y: 0 });
  const mainSizeRef = useRef({ width: 0, height: 0 });

  const [zoomState, setZoomState] = useState({
    show: false,
    lensSize: LENS_DEFAULT,
    lensPos: { x: 0, y: 0 },
    panelSize: { width: 0, height: 0 },
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  // Track container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        const h = Math.round(entry.contentRect.height);
        mainSizeRef.current = { width: w, height: h };
        setZoomState((prev) => ({ ...prev, panelSize: { width: w, height: h } }));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleWheel = useCallback((e: WheelEvent) => {
    if (!zoomState.show) return;
    e.preventDefault();
    e.stopPropagation();
    
    if (wheelLock.current) return;
    wheelLock.current = true;
    setTimeout(() => { wheelLock.current = false; }, 16);

    const { width, height } = mainSizeRef.current;
    const lensMax = Math.min(width || 400, height || 400);

    const delta = e.deltaY < 0 ? -15 : 15;
    const nextSize = Math.min(lensMax, Math.max(LENS_MIN, lensSizeRef.current + delta));

    lensSizeRef.current = nextSize;

    // Constrain position after resize
    const pos = lensPosRef.current;
    const cx = Math.max(0, Math.min(pos.x, width - nextSize));
    const cy = Math.max(0, Math.min(pos.y, height - nextSize));
    lensPosRef.current = { x: cx, y: cy };

    setZoomState((prev) => ({
      ...prev,
      lensSize: nextSize,
      lensPos: { x: cx, y: cy },
    }));
  }, [zoomState.show]);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.addEventListener("wheel", handleWheel, { passive: false });
    }
    return () => el?.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const handleMouseMove = (e: React.MouseEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ls = lensSizeRef.current;
    
    let x = e.clientX - rect.left - ls / 2;
    let y = e.clientY - rect.top - ls / 2;
    
    x = Math.max(0, Math.min(x, rect.width - ls));
    y = Math.max(0, Math.min(y, rect.height - ls));

    lensPosRef.current = { x, y };
    setZoomState((prev) => ({ ...prev, show: true, lensPos: { x, y }, lensSize: ls }));
  };

  const images = [
    product?.picture,
    product?.picture2 || product?.picture_2,
    product?.picture3 || product?.picture_3,
    product?.picture4 || product?.picture_4,
    product?.hinh_anh,
  ].filter(Boolean);

  if (images.length === 0) return null;

  const activeSrc = appendImageUrlFromAPI({ src: images[activeImg], size: "xl" });
  const { show, lensSize, lensPos, panelSize } = zoomState;
  
  // Calculate transformation for preview
  const liveRect = show && containerRef.current ? containerRef.current.getBoundingClientRect() : null;
  const zoomFactor = (panelSize.width || 400) / Math.max(lensSize, 1);
  const imgTx = (panelSize.width / 2) - (lensPos.x + lensSize / 2) * zoomFactor;
  const imgTy = (panelSize.height / 2) - (lensPos.y + lensSize / 2) * zoomFactor;

  // Portal Zoom Preview
  const zoomPanel = mounted && show && liveRect && (
    createPortal(
      <div 
        className={styles.zoomPreview}
        style={{
          left: liveRect.right + 20,
          top: liveRect.top,
          width: panelSize.width,
          height: panelSize.height,
        }}
      >
        <img 
          src={activeSrc} 
          alt="zoom" 
          style={{
            width: panelSize.width, 
            height: panelSize.height, 
            objectFit: "contain",
            position: "absolute", 
            top: 0, 
            left: 0, 
            transformOrigin: "0 0",
            transform: `translate(${imgTx}px, ${imgTy}px) scale(${zoomFactor})`
          }} 
        />
      </div>,
      document.body
    )
  );

  const [thumbOffset, setThumbOffset] = useState(0);
  const THUMB_VISIBLE = 4;
  const visibleThumbs = images.slice(thumbOffset, thumbOffset + THUMB_VISIBLE);

  return (
    <div className={styles.container}>
      <div className={styles.thumbCol}>
        <button 
          className={styles.navBtn} 
          onClick={() => setThumbOffset(o => Math.max(0, o - 1))}
          disabled={thumbOffset === 0}
        >
          <ChevronUp size={16} />
        </button>
        
        <div className={styles.thumbnails}>
          {visibleThumbs.map((img, idx) => {
            const actualIdx = idx + thumbOffset;
            return (
              <button
                key={actualIdx}
                className={`${styles.thumbBtn} ${activeImg === actualIdx ? styles.active : ""}`}
                onMouseEnter={() => setActiveImg(actualIdx)}
              >
                <Image
                  src={appendImageUrlFromAPI({ src: img, size: "s" })}
                  alt={`${product?.ten_vt} ${actualIdx + 1}`}
                  fill
                  className={styles.thumbImg}
                />
              </button>
            );
          })}
        </div>

        <button 
          className={styles.navBtn}
          onClick={() => setThumbOffset(o => Math.min(Math.max(0, images.length - THUMB_VISIBLE), o + 1))}
          disabled={thumbOffset >= images.length - THUMB_VISIBLE}
        >
          <ChevronDown size={16} />
        </button>
      </div>

      <div
        className={styles.mainImageWrap}
        ref={containerRef}
        onMouseEnter={() => setZoomState(p => ({ ...p, show: true }))}
        onMouseLeave={() => setZoomState(p => ({ ...p, show: false }))}
        onMouseMove={handleMouseMove}
        onClick={() => setIsLightboxOpen(true)}
      >
        <Image
          src={activeSrc}
          alt={product?.ten_vt}
          fill
          className={styles.mainImage}
          priority
        />
        {show && (
          <div 
            className={styles.zoomLens}
            style={{
              left: lensPos.x,
              top: lensPos.y,
              width: lensSize,
              height: lensSize,
            }}
          />
        )}
      </div>

      {zoomPanel}

      {isLightboxOpen && (
        <div className={styles.lightbox} onClick={() => setIsLightboxOpen(false)}>
          <button className={styles.closeBtn}><X size={40} /></button>
          <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <Image
              src={appendImageUrlFromAPI({ src: images[activeImg], size: "xl" })}
              alt={product?.ten_vt}
              width={1200}
              height={1200}
              className={styles.lightboxImage}
            />
          </div>
        </div>
      )}
    </div>
  );
}
